import { useCallback, useState } from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import BoltIcon from '@/material-icons/400-24px/bolt-fill.svg?react';
import { apiRequestPost } from 'mastodon/api';
import { Button } from 'mastodon/components/button';
import { Icon } from 'mastodon/components/icon';
import { NavigationFocusTarget } from 'mastodon/components/navigation_focus_target';
import { useZapWalletStatus } from 'mastodon/hooks/useZapWalletStatus';
import { useAppSelector } from 'mastodon/store';
import lightningService from 'mastodon/utils/lightning_service';

const messages = defineMessages({
  title: { id: 'zap_modal.title', defaultMessage: 'Zap {name}' },
  cancel: { id: 'confirmation_modal.cancel', defaultMessage: 'Cancel' },
  send: { id: 'zap_modal.send', defaultMessage: 'Zap {amount} sats' },
  customAmount: {
    id: 'zap_modal.custom_amount',
    defaultMessage: 'Custom amount (sats)',
  },
  comment: { id: 'zap_modal.comment', defaultMessage: 'Comment (optional)' },
  sent: { id: 'zap_modal.sent', defaultMessage: 'Zap sent!' },
  walletConnected: {
    id: 'zap_modal.wallet_connected',
    defaultMessage: 'Wallet connected, this will send instantly',
  },
});

const DEFAULT_PRESET_AMOUNTS = [21, 210, 1000, 5000, 21000];

const formatPreset = (n: number) => (n >= 1000 ? `${n / 1000}k` : String(n));

export const ZapModal: React.FC<{
  statusId?: string;
  lightningAddress: string;
  recipientName: string;
  onClose: () => void;
}> = ({ statusId, lightningAddress, recipientName, onClose }) => {
  const intl = useIntl();
  const { connected: walletConnected } = useZapWalletStatus();

  // The zap deck (Settings > Zap presets) lets users customize this list;
  // fall back to sensible defaults until it's loaded or if it's empty.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  const zapDeck = useAppSelector((state: any) => state.zap_deck?.toArray()) as
    | number[]
    | undefined;
  const presetAmounts: number[] =
    zapDeck && zapDeck.length > 0 ? zapDeck : DEFAULT_PRESET_AMOUNTS;

  const [amount, setAmount] = useState(presetAmounts[0] ?? 21);
  const [customAmount, setCustomAmount] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const effectiveAmount = customAmount ? parseInt(customAmount, 10) || 0 : amount;

  const handlePresetClick = useCallback((value: number) => {
    setAmount(value);
    setCustomAmount('');
  }, []);

  const handleZap = useCallback(() => {
    if (effectiveAmount < 1) {
      setError('Amount must be at least 1 sat');
      return;
    }

    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const { invoice, verify } = await lightningService.fetchInvoice(
          lightningAddress,
          effectiveAmount,
          comment || undefined,
        );
        const payment = await lightningService.payInvoice(invoice, verify);

        if (!payment) {
          setLoading(false);
          return;
        }

        // Profile zaps (no statusId) have nothing to attach a receipt to,
        // so they're sent peer-to-peer over Lightning and left untracked,
        // same as in typent.
        if (statusId) {
          await apiRequestPost(`v1/statuses/${statusId}/zap`, {
            bolt11: payment.invoice,
            preimage: payment.preimage,
            comment: comment || undefined,
          });
        }

        setSent(true);
        setTimeout(onClose, 1800);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send zap');
      } finally {
        setLoading(false);
      }
    })();
  }, [effectiveAmount, lightningAddress, comment, statusId, onClose]);

  return (
    <div className='modal-root__modal safety-action-modal zap-modal'>
      <div className='safety-action-modal__top'>
        <div className='safety-action-modal__header'>
          <div className='safety-action-modal__header__icon zap-modal__icon'>
            <Icon icon={BoltIcon} id='bolt' />
          </div>

          <div>
            <NavigationFocusTarget as='h1'>
              {intl.formatMessage(messages.title, { name: recipientName })}
            </NavigationFocusTarget>
          </div>
        </div>

        <div className='zap-modal__content'>
          {sent ? (
            <div className='zap-modal__sent'>
              <Icon icon={BoltIcon} id='bolt' />
              <p>{intl.formatMessage(messages.sent)}</p>
            </div>
          ) : (
            <>
              <div className='zap-modal__amount'>
                {effectiveAmount > 0 ? effectiveAmount.toLocaleString() : '-'}{' '}
                <span className='zap-modal__amount__unit'>sats</span>
              </div>

              <div className='zap-modal__presets'>
                {presetAmounts.map((value) => (
                  <button
                    key={value}
                    type='button'
                    className={
                      amount === value && !customAmount
                        ? 'zap-modal__preset zap-modal__preset--active'
                        : 'zap-modal__preset'
                    }
                    onClick={() => {
                      handlePresetClick(value);
                    }}
                  >
                    {formatPreset(value)}
                  </button>
                ))}
              </div>

              <input
                type='number'
                className='zap-modal__input'
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                }}
                placeholder={intl.formatMessage(messages.customAmount)}
                min={1}
              />

              <input
                type='text'
                className='zap-modal__input'
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                }}
                placeholder={intl.formatMessage(messages.comment)}
                maxLength={140}
              />

              {walletConnected && (
                <div className='zap-modal__wallet-status'>
                  {intl.formatMessage(messages.walletConnected)}
                </div>
              )}

              {error && <div className='zap-modal__error'>{error}</div>}
            </>
          )}
        </div>
      </div>

      {!sent && (
        <div className='safety-action-modal__bottom'>
          <div className='safety-action-modal__actions'>
            <div className='spacer' />

            <button onClick={onClose} className='link-button' type='button'>
              <FormattedMessage {...messages.cancel} />
            </button>

            <Button
              onClick={handleZap}
              disabled={effectiveAmount < 1}
              loading={loading}
              text={intl.formatMessage(messages.send, {
                amount: effectiveAmount > 0 ? effectiveAmount.toLocaleString() : '-',
              })}
            />
          </div>
        </div>
      )}
    </div>
  );
};
